/* Range/Menu API data types */

// Kept in sync with backend Orderly.Inventory.Range.Data.Enums.MenuType.
export enum MenuType {
  Breakfast = 'Breakfast',
  Brunch = 'Brunch',
  Lunch = 'Lunch',
  Dinner = 'Dinner',
  AllDay = 'AllDay',
  Drinks = 'Drinks',
  Kids = 'Kids',
  LateNight = 'LateNight',
  Other = 'Other',
}

// Kept in sync with backend Orderly.Inventory.Range.Data.Enums.MenuStatus (stored publishing state).
export enum MenuStatus {
  Draft = 'Draft',
  Published = 'Published',
}

// Derived by the backend from Status + StartDate/EndDate; never stored/edited directly.
export enum MenuDisplayStatus {
  Draft = 'Draft',
  Scheduled = 'Scheduled',
  Active = 'Active',
  Expired = 'Expired',
}

// UI-only publishing choice, mapped to Status/StartDate/EndDate before saving (see spec section 13.2).
export type MenuPublishMode = 'draft' | 'publishNow' | 'schedule';

export const DAYS_OF_WEEK: readonly number[] = [0, 1, 2, 3, 4, 5, 6]; // System.DayOfWeek numeric values (0 = Sunday)

export interface PagedData<T> {
  totalCount: number;
  skip: number;
  take: number;
  data: T[];
}

export interface RangeDto {
  id: number;
  franchiseId: number;
  name: string;
  description?: string | null;
  menuCount: number;
  createdBy: number;
  createdDate: string;
  modifiedBy?: number | null;
  modifiedDate?: string | null;
}

export interface RangeStatsDto {
  totalRanges: number;
  emptyRanges: number;
  scheduledOnlyRanges: number;
  newRanges: number;
}

export interface MenuDto {
  id: number;
  franchiseId: number;
  name: string;
  description?: string | null;
  menuType?: MenuType | null;
  status: MenuStatus;
  startDate?: string | null;
  endDate?: string | null;
  displayStatus: MenuDisplayStatus;
  servicePeriodCount: number;
  recipeCount: number;
  servicePeriodNames: string[];
  createdBy: number;
  createdDate: string;
  modifiedBy?: number | null;
  modifiedDate?: string | null;
}

export interface MenuStatsDto {
  totalMenus: number;
  activeMenus: number;
  scheduledMenus: number;
  draftMenus: number;
}

export interface RangeMenuDto {
  menuId: number;
  menuName: string;
}

export interface MenuRangeDto {
  rangeId: number;
  rangeName: string;
}

export interface MenuRecipeDto {
  recipeId: number;
  recipeName: string;
  category?: string | null;
  displayOrder: number;
}

export interface RecipeCandidateDto {
  recipeId: number;
  recipeName: string;
  category?: string | null;
}

export interface ServicePeriodDto {
  id: number;
  menuId: number;
  name: string;
  startTime: string; // "HH:mm:ss"
  endTime: string;
  daysOfWeek: number[];
}

export interface UpsertServicePeriod {
  name: string;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
}

export interface CreateRangeDto {
  franchiseId: number;
  name: string;
  description?: string | null;
  menuIds: number[];
}

export interface UpdateRangeDto {
  name: string;
  description?: string | null;
  menuIds: number[];
}

export interface CreateMenuDto {
  franchiseId: number;
  name: string;
  description?: string | null;
  menuType?: MenuType | null;
  status: MenuStatus;
  startDate?: string | null;
  endDate?: string | null;
  rangeIds: number[];
  recipeIds: number[];
  servicePeriods: UpsertServicePeriod[];
}

export interface UpdateMenuDto {
  name: string;
  description?: string | null;
  menuType?: MenuType | null;
  status: MenuStatus;
  startDate?: string | null;
  endDate?: string | null;
  rangeIds: number[];
  recipeIds: number[];
  servicePeriods: UpsertServicePeriod[];
}

export interface ListRangesParams {
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface ListMenusParams {
  search?: string;
  menuType?: MenuType;
  displayStatus?: MenuDisplayStatus;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface ListAvailableRecipesParams {
  franchiseId: number;
  search?: string;
  page?: number;
  pageSize?: number;
}
